import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as quotesApi from '../api/quotes';
import * as clientsApi from '../api/clients';
import * as templatesApi from '../api/templates';
import * as leadsApi from '../api/leads';
import 'react-quill-new/dist/quill.snow.css';

// Import Quote Builder specific components
import QuoteSectionCard from '../features/quote-builder/QuoteSectionCard';
import QuoteCalculationsDisplay from '../features/quote-builder/QuoteCalculationsDisplay';
import QuoteSectionEditorModal from '../features/quote-builder/QuoteSectionEditorModal';
import RegularLineItemModal from '../features/quote-builder/RegularLineItemModal';
import TemplateSelectionModal from '../features/quote-builder/TemplateSelectionModal';
import MessageModal from '../components/MessageModal';
import PageHeader from '../components/PageHeader';

function QuoteFormPage() {
    const { loading: authLoading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams(); // Get quote ID from URL params (if editing)
    const isEditing = !!id; // Boolean to check if we are in edit mode

    // Get leadId and clientId from URL search params (e.g., /quotes/new?leadId=123 or /quotes/new?clientId=456)
    const searchParams = new URLSearchParams(location.search);
    const leadId = searchParams.get('leadId');
    const clientIdFromUrl = searchParams.get('clientId');
    const opportunityIdFromUrl = searchParams.get('opportunityId');

    const [formData, setFormData] = useState({
        clientId: '',
        quoteName: '',
        overallTaxRate: 0.00,
        discountType: 'percentage',
        discountValue: 0,
        expirationDate: '',
        quoteTermsConditions: '',
        sections: [],
        regularLineItems: [], // New state for regular line items
        status: 'draft',
        isArchived: false,
        publicAccessToken: null,
    });
    const [clients, setClients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [leadData, setLeadData] = useState(null);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(true);

    const [isSectionEditorModalOpen, setIsSectionEditorModalOpen] = useState(false);
    const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
    const [isTemplateSelectorModalOpen, setIsTemplateSelectorModalOpen] = useState(false);
    const [currentEditingSectionId, setCurrentEditingSectionId] = useState(null);
    const [currentEditingLineItem, setCurrentEditingLineItem] = useState(null);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);

    // --- Initial Data Fetch ---
    useEffect(() => {
        const fetchInitialData = async () => {
            if (authLoading) {
                setLoading(false);
                return;
            }

            try {
                setError(null);
                setSuccess(null);
                setLoading(true);

                const clientsResponse = await clientsApi.fetchClients();
                setClients(clientsResponse.clients || []);

                const templatesResponse = await templatesApi.fetchTemplates();
                setTemplates(templatesResponse.templates || []);

                // If editing a quote that was created from a lead, fetch lead data for display
                if (leadId && isEditing) {
                    try {
                        const leadResponse = await leadsApi.fetchLead(leadId);
                        const lead = leadResponse.lead;
                        setLeadData(lead);
                    } catch (leadError) {
                        console.error('Failed to load lead data:', leadError);
                        // Don't show error - lead data is just for display context
                    }
                }

                if (isEditing) {
                    console.log('QUOTE FORM DEBUG - Loading quote in edit mode, ID:', id);
                    const quoteResponse = await quotesApi.fetchQuoteById(id);
                    const quoteDataForEdit = quoteResponse.quote;
                    console.log('QUOTE FORM DEBUG - Loaded existing quote:', quoteDataForEdit);

                    // Convert regularLineItems from snake_case to camelCase if needed
                    const normalizedRegularLineItems = (quoteDataForEdit.regularLineItems || []).map(item => ({
                        id: item.id,
                        itemName: item.itemName || item.item_name,
                        description: item.description || '',
                        price: parseFloat(item.price) || 0,
                        isTaxable: item.isTaxable !== undefined ? item.isTaxable : Boolean(item.is_taxable),
                        sortOrder: item.sortOrder !== undefined ? item.sortOrder : item.sort_order
                    }));

                    setFormData({
                        clientId: quoteDataForEdit.clientId || '',
                        quoteName: quoteDataForEdit.quoteName || '',
                        overallTaxRate: parseFloat(quoteDataForEdit.overallTaxRate) || 0,
                        discountType: quoteDataForEdit.discountType || 'percentage',
                        discountValue: parseFloat(quoteDataForEdit.discountValue) || 0,
                        expirationDate: quoteDataForEdit.expirationDate ? quoteDataForEdit.expirationDate.split('T')[0] : '',
                        quoteTermsConditions: quoteDataForEdit.quoteTermsConditions || user?.defaultTermsConditions || '',
                        sections: quoteDataForEdit.sections || [],
                        regularLineItems: normalizedRegularLineItems, // Use normalized data
                        status: quoteDataForEdit.status || 'draft',
                        isArchived: quoteDataForEdit.isArchived || false,
                        publicAccessToken: quoteDataForEdit.publicAccessToken || null,
                    });
                } else {
                    // Initialize new quote form with URL parameters if provided
                    setFormData(prev => ({
                        ...prev,
                        clientId: clientIdFromUrl || '',
                        quoteTermsConditions: user?.defaultTermsConditions || '',
                        expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                    }));
                }
            } catch (err) {
                console.error('Failed to load initial data:', err);
                setError(err.message || 'Failed to load initial data for quote form.');
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditing, leadId, clientIdFromUrl, authLoading, user?.defaultTermsConditions]);

    useEffect(() => {
        if (user && !formData.quoteTermsConditions) {
            setFormData(prev => ({
                ...prev,
                quoteTermsConditions: user.defaultTermsConditions || '',
            }));
        }
    }, [user, formData.quoteTermsConditions]);

    // --- Handlers for Quote-level Fields ---
    const handleQuoteFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // --- Handlers for Regular Line Items ---
    const handleOpenLineItemModal = (item = null) => {
        setCurrentEditingLineItem(item);
        setIsLineItemModalOpen(true);
        setShowAddMenu(false);
    };

    const handleSaveLineItem = (lineItemData) => {
        setFormData(prev => {
            if (lineItemData.id) {
                // Editing existing item
                return {
                    ...prev,
                    regularLineItems: prev.regularLineItems.map(item =>
                        item.id === lineItemData.id ? lineItemData : item
                    )
                };
            } else {
                // Adding new item
                const newItem = {
                    ...lineItemData,
                    id: Math.random(),
                    sortOrder: prev.sections.length + prev.regularLineItems.length
                };
                return {
                    ...prev,
                    regularLineItems: [...prev.regularLineItems, newItem]
                };
            }
        });
        setIsLineItemModalOpen(false);
        setCurrentEditingLineItem(null);
    };

    const handleRemoveRegularLineItem = (id) => {
        setFormData(prev => ({
            ...prev,
            regularLineItems: prev.regularLineItems.filter(item => item.id !== id)
        }));
    };

    const handleDuplicateRegularLineItem = (id) => {
        setFormData(prevData => {
            const itemToDuplicate = prevData.regularLineItems.find(item => item.id === id);
            if (!itemToDuplicate) return prevData;

            const newId = Date.now();
            const newSortOrder = prevData.regularLineItems.length > 0
                ? Math.max(...prevData.regularLineItems.map(item => item.sortOrder || 0)) + 1
                : 0;

            const duplicatedItem = {
                ...itemToDuplicate,
                id: newId,
                itemName: `${itemToDuplicate.itemName} (Copy)`,
                sortOrder: newSortOrder,
            };

            return {
                ...prevData,
                regularLineItems: [...prevData.regularLineItems, duplicatedItem],
            };
        });
    };

    // --- Handlers for Sections ---
    const handleAddSection = async (selectedTemplateId, dimensions) => {
        setIsTemplateSelectorModalOpen(false);
        setLoading(true);

        let selectedTemplate = null;
        let templateItems = [];

        if (selectedTemplateId) {
            try {
                const response = await templatesApi.fetchTemplateById(selectedTemplateId);
                selectedTemplate = response.template;
                templateItems = selectedTemplate.items || [];
            } catch (err) {
                console.error('Failed to fetch template details:', err);
                setError(err.message || 'Failed to load template details.');
                setLoading(false);
                return;
            }
        }

        const newSectionId = Date.now();
        const newSortOrder = formData.sections.length > 0 ? Math.max(...formData.sections.map(s => s.sortOrder || 0)) + 1 : 0;

        let newSection = {
            id: newSectionId,
            name: selectedTemplate ? selectedTemplate.name : 'Custom Section',
            description: selectedTemplate ? selectedTemplate.description || '' : '',
            templateId: selectedTemplate ? selectedTemplate.id : null,
            sortOrder: newSortOrder,
            lineItems: selectedTemplate ? (
                Array.isArray(templateItems) ?
                    templateItems.map((item, index) => ({
                        id: Math.random(),
                        itemName: item.itemName || '',
                        unitType: item.unitType || 'unit',
                        unitPrice: parseFloat(item.unitPrice) || 0,
                        quantity: 0,
                        description: item.description || '',
                        isTaxable: item.isTaxable !== undefined ? !!item.isTaxable : true,
                        sortOrder: item.sortOrder !== undefined ? item.sortOrder : index
                    })) : []
            ) : [ // Default items for custom section
                {
                    id: Math.random(),
                    itemName: 'Labor',
                    unitType: 'unit',
                    unitPrice: 1,
                    quantity: 0,
                    description: 'Cost for labor',
                    isTaxable: true,
                    sortOrder: 0
                },
                {
                    id: Math.random(),
                    itemName: 'Materials',
                    unitType: 'unit',
                    unitPrice: 1,
                    quantity: 0,
                    description: 'Cost for materials',
                    isTaxable: true,
                    sortOrder: 1
                }
            ],
        };

        setFormData(prevData => ({
            ...prevData,
            sections: [...prevData.sections, newSection],
        }));

        setLoading(false);
        setCurrentEditingSectionId(newSectionId);
        setIsSectionEditorModalOpen(true);
    };

    const handleEditSection = (sectionId) => {
        setCurrentEditingSectionId(sectionId);
        setIsSectionEditorModalOpen(true);
    };

    const handleSaveSection = (updatedData) => {
        setFormData(prevData => ({
            ...prevData,
            sections: prevData.sections.map(section =>
                section.id === currentEditingSectionId
                    ? { ...section, name: updatedData.name, description: updatedData.description, lineItems: updatedData.items }
                    : section
            ),
        }));
        setIsSectionEditorModalOpen(false);
        setCurrentEditingSectionId(null);
    };

    const handleRemoveSection = (sectionId) => {
        if (window.confirm('Are you sure you want to remove this section from the quote?')) {
            setFormData(prevData => ({
                ...prevData,
                sections: prevData.sections.filter(section => section.id !== sectionId),
            }));
        }
    };

    const handleDuplicateSection = (sectionId) => {
        setFormData(prevData => {
            const sectionToDuplicate = prevData.sections.find(s => s.id === sectionId);
            if (!sectionToDuplicate) return prevData;

            const newId = Date.now();
            const newSortOrder = formData.sections.length > 0 ? Math.max(...formData.sections.map(s => s.sortOrder || 0)) + 1 : 0;

            const duplicatedSection = {
                ...sectionToDuplicate,
                id: newId,
                name: `${sectionToDuplicate.name} (Copy)`,
                sortOrder: newSortOrder,
                lineItems: Array.isArray(sectionToDuplicate.lineItems) ? sectionToDuplicate.lineItems.map(item => ({ ...item, id: Math.random() })) : [],
            };

            return {
                ...prevData,
                sections: [...prevData.sections, duplicatedSection],
            };
        });
    };

    // --- Calculations for Display ---
    const calculateSectionTotalsForDisplay = (section) => {
        let subtotal = 0;
        let taxableSubtotal = 0;
        if (!Array.isArray(section.lineItems)) return { subtotal: 0, taxableSubtotal: 0 };

        section.lineItems.forEach(item => {
            const itemTotal = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
            subtotal += itemTotal;
            if (item.isTaxable) {
                taxableSubtotal += itemTotal;
            }
        });
        return { subtotal, taxableSubtotal };
    };

    const calculateOverallTotals = () => {
        let overallSubtotal = 0;
        let overallTaxableSubtotal = 0;

        if (!Array.isArray(formData.sections) && !Array.isArray(formData.regularLineItems)) return { subtotal: "0.00", calculatedDiscountAmount: "0.00", calculatedTaxAmount: "0.00", grandTotal: "0.00" };

        // Calculate totals from sections
        if (Array.isArray(formData.sections)) {
            formData.sections.forEach(section => {
                const { subtotal, taxableSubtotal } = calculateSectionTotalsForDisplay(section);
                overallSubtotal += subtotal;
                overallTaxableSubtotal += taxableSubtotal;
            });
        }

        // Calculate totals from regular line items
        if (Array.isArray(formData.regularLineItems)) {
            formData.regularLineItems.forEach(item => {
                const itemTotal = parseFloat(item.price) || 0;
                overallSubtotal += itemTotal;
                if (item.isTaxable) {
                    overallTaxableSubtotal += itemTotal;
                }
            });
        }

        const discountValue = parseFloat(formData.discountValue) || 0;
        const taxRate = parseFloat(formData.overallTaxRate) || 0;

        const calculatedDiscountAmount = formData.discountType === 'percentage'
            ? overallSubtotal * (discountValue / 100)
            : discountValue;

        const subtotalAfterDiscount = overallSubtotal - calculatedDiscountAmount;
        const taxableSubtotalAfterDiscount = overallTaxableSubtotal * (subtotalAfterDiscount / overallSubtotal) || 0;

        const calculatedTaxAmount = taxableSubtotalAfterDiscount * taxRate;
        const grandTotal = subtotalAfterDiscount + calculatedTaxAmount;

        return {
            subtotal: overallSubtotal.toFixed(2),
            taxableSubtotal: overallTaxableSubtotal.toFixed(2),
            calculatedDiscountAmount: calculatedDiscountAmount.toFixed(2),
            calculatedTaxAmount: calculatedTaxAmount.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
        };
    };

    const overallTotals = calculateOverallTotals();

    // --- Form Submission (Create/Update Quote) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        if (!formData.clientId) {
            setError('Please select a client.');
            setLoading(false);
            return;
        }
        // Allow quotes to be saved without sections (they can be added later)

        // Recalculate line item totals before submission to ensure consistency
        // NOTE: This calculation must mirror the backend logic in Quote._calculateItemTotals
        // in server/models/Quote.js to prevent discrepancies that could cause validation errors.
        const updatedSections = formData.sections.map(section => {
            const updatedLineItems = section.lineItems.map(item => {
                const unitPrice = parseFloat(item.unitPrice || item.unit_price) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                const itemTotal = unitPrice * quantity;
                return {
                    // Use backend-expected field names consistently
                    id: item.id,
                    itemName: item.itemName || item.item_name,
                    unitType: item.unitType || item.unit_type,
                    unitPrice: unitPrice,
                    quantity: quantity,
                    description: item.description || '',
                    isTaxable: item.isTaxable !== undefined ? item.isTaxable : item.is_taxable,
                    lineItemTotal: Math.round(itemTotal * 100) / 100,
                    sortOrder: item.sortOrder || item.sort_order || 0
                };
            });

            // Calculate section totals
            let subtotal = 0;
            let taxableSubtotal = 0;
            updatedLineItems.forEach(item => {
                const itemTotal = parseFloat(item.lineItemTotal) || 0;
                subtotal += itemTotal;
                if (item.isTaxable) {
                    taxableSubtotal += itemTotal;
                }
            });

            return {
                id: section.id,
                name: section.name,
                description: section.description,
                templateId: section.templateId || section.template_id,
                sortOrder: section.sortOrder || section.sort_order || 0,
                lineItems: updatedLineItems,
                section_subtotal: Math.round(subtotal * 100) / 100,
                section_taxable_subtotal: Math.round(taxableSubtotal * 100) / 100
            };
        });

        // In QuoteFormPage.js, replace the handleSubmit function's dataToSubmit section:

        // Process regular line items for submission
        const processedRegularLineItems = formData.regularLineItems.map(item => {
            return {
                id: item.id,
                itemName: item.itemName,
                description: item.description || '',
                price: parseFloat(item.price) || 0,
                isTaxable: item.isTaxable !== undefined ? item.isTaxable : true,
                sortOrder: item.sortOrder || 0
            };
        });

        try {
            const dataToSubmit = {
                clientId: parseInt(formData.clientId),
                opportunityId: opportunityIdFromUrl ? parseInt(opportunityIdFromUrl) : null,
                quoteName: formData.quoteName,
                overallTaxRate: parseFloat(formData.overallTaxRate),
                discountType: formData.discountType,
                discountValue: parseFloat(formData.discountValue),
                expirationDate: formData.expirationDate || null,
                quoteTermsConditions: formData.quoteTermsConditions,
                status: formData.status,
                isArchived: formData.isArchived,
                sections: updatedSections,
                regularLineItems: processedRegularLineItems // Include processed regular line items
            };


            if (isEditing) {
                await quotesApi.updateQuote(id, dataToSubmit);
                setSuccess('Quote updated successfully!');
            } else {
                console.log('QUOTE FORM DEBUG - Creating new quote (this should not happen from pipeline)');
                const response = await quotesApi.createQuote(dataToSubmit);
                setSuccess('Quote created successfully!');
                navigate(`/quotes/edit/${response.quote.id}`);
            }
        } catch (err) {
            console.error('Quote submission failed:', err);
            setError(err.message || 'Failed to save quote.');
        } finally {
            setLoading(false);
        }
    };

    // --- Send Quote via Email ---
    const handleSendEmail = () => {
        if (!formData || !formData.publicAccessToken) {
            alert("Error: This quote does not have a public access link or quote data is not loaded.");
            return;
        }

        if (!user || !user.companyName) {
            alert("Error: Company details not found for the current user.");
            return;
        }

        const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));
        if (!selectedClient || !selectedClient.email) {
            alert("Error: The selected client does not have an email address.");
            return;
        }

        // Open the email modal with quote_sent template
        setShowEmailModal(true);
    };

    // --- Manual Accept (No Signature) ---
    const handleManualAccept = async () => {
        if (!window.confirm("Are you sure you want to mark this quote as ACCEPTED without a client signature?\n\nWARNING: The client will still receive an automated 'Quote Accepted' email unless you have disabled automated emails for this opportunity.")) {
            return;
        }

        try {
            setLoading(true);
            await quotesApi.updateQuoteStatusOrArchive(id, { status: 'accepted' });
            setFormData(prev => ({ ...prev, status: 'accepted' }));
            setSuccess('Quote marked as accepted successfully!');
        } catch (err) {
            console.error('Failed to accept quote:', err);
            setError(err.message || 'Failed to accept quote.');
        } finally {
            setLoading(false);
        }
    };

    const currentEditingSection = currentEditingSectionId
        ? formData.sections.find(s => s.id === currentEditingSectionId)
        : null;

    if (authLoading) {
        return <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4"><div className="text-xl text-gray-700">Loading Authentication...</div></div>;
    }

    if (loading && !isSectionEditorModalOpen && !isTemplateSelectorModalOpen) {
        return <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4"><div className="text-xl text-gray-700">Loading Quote Data...</div></div>;
    }

    if (error && !isEditing && (!clients.length)) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center">
                    {error}
                    <p className="mt-2 text-sm">Please ensure you have clients created first.</p>
                    <Link to="/clients/new" className="text-primary-600 underline mt-2 block">Add a New Client</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
                    <PageHeader title={isEditing ? `Editing Quote: ${formData.quoteName}` : 'Create New Quote'} />

                    {error && <div className="mt-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" onClick={() => setError(null)}>{error}</div>}
                    {success && <div className="mt-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-4" onClick={() => setSuccess(null)}>{success}</div>}

                    {/* Lead Information Banner */}
                    {leadData && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                                        Editing quote for lead: {leadData.client_name || leadData.name}
                                    </h4>
                                    <div className="text-sm text-blue-800 space-y-1">
                                        <p><strong>Project:</strong> {leadData.project_name}</p>
                                        {leadData.preferred_date && (
                                            <p><strong>Preferred Date:</strong> {
                                                leadData.preferred_date.includes('T') && leadData.preferred_date.includes('Z')
                                                    ? new Date(leadData.preferred_date.split('T')[0] + 'T00:00:00').toLocaleDateString()
                                                    : new Date(leadData.preferred_date + 'T00:00:00').toLocaleDateString()
                                            }</p>
                                        )}
                                        {leadData.preferred_time_range && (
                                            <p><strong>Preferred Time:</strong> {leadData.preferred_time_range}</p>
                                        )}
                                        {leadData.service_details && (
                                            <p><strong>Service Details:</strong> {leadData.service_details}</p>
                                        )}
                                        {leadData.estimated_value && (
                                            <p><strong>Estimated Value:</strong> ${leadData.estimated_value.toLocaleString()}</p>
                                        )}
                                        <p><strong>Stage:</strong> {leadData.stage.replace('_', ' ').toUpperCase()}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="quoteName" className="block text-sm font-medium text-slate-700">Quote Name</label>
                                <input type="text" id="quoteName" name="quoteName" value={formData.quoteName} onChange={handleQuoteFieldChange} placeholder="e.g., Doe Residence - Interior Paint" className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm" required />
                            </div>
                            <div>
                                <label htmlFor="clientId" className="block text-sm font-medium text-slate-700">Select Client</label>
                                <select id="clientId" name="clientId" value={formData.clientId} onChange={handleQuoteFieldChange} className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm bg-white" required>
                                    <option value="">-- Select a Client --</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>{client.name} ({client.email || client.phone})</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Unified Sections and Line Items */}
                        <div className="pt-6 border-t border-slate-200">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                                <h3 className="text-xl sm:text-2xl font-semibold text-slate-800">Quote Items</h3>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddMenu(!showAddMenu)}
                                        className="w-full sm:w-auto px-6 py-3 sm:px-4 sm:py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 flex items-center justify-center space-x-2 min-h-[44px]"
                                    >
                                        <span>+ Add Item</span>
                                        <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    {showAddMenu && (
                                        <div className="absolute right-0 mt-2 w-full sm:w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsTemplateSelectorModalOpen(true);
                                                    setShowAddMenu(false);
                                                }}
                                                className="w-full px-4 py-4 sm:py-3 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 min-h-[56px]"
                                            >
                                                <svg className="w-6 h-6 sm:w-5 sm:h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <div>
                                                    <div className="font-medium text-gray-900 text-base sm:text-sm">Add Section</div>
                                                    <div className="text-sm sm:text-xs text-gray-500">Group multiple items together</div>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleOpenLineItemModal()}
                                                className="w-full px-4 py-4 sm:py-3 text-left hover:bg-gray-50 flex items-center space-x-3 min-h-[56px]"
                                            >
                                                <svg className="w-6 h-6 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                <div>
                                                    <div className="font-medium text-gray-900 text-base sm:text-sm">Add Line Item</div>
                                                    <div className="text-sm sm:text-xs text-gray-500">Single standalone item</div>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {formData.sections.length === 0 && formData.regularLineItems.length === 0 ? (
                                <p className="text-slate-500 text-center py-8 border border-dashed border-slate-300 rounded-lg">Add items to your quote using the button above.</p>
                            ) : (
                                <div className="space-y-4">
                                    {/* Render unified sorted items */}
                                    {(() => {
                                        const unifiedItems = [
                                            ...formData.sections.map(section => ({ type: 'section', data: section, sortOrder: section.sortOrder || 0 })),
                                            ...formData.regularLineItems.map(item => ({ type: 'lineItem', data: item, sortOrder: item.sortOrder || 0 }))
                                        ].sort((a, b) => a.sortOrder - b.sortOrder);

                                        return unifiedItems.map((item, index) => {
                                            if (item.type === 'section') {
                                                return (
                                                    <QuoteSectionCard
                                                        key={`section-${item.data.id}`}
                                                        section={item.data}
                                                        onEdit={handleEditSection}
                                                        onRemove={handleRemoveSection}
                                                        onDuplicate={handleDuplicateSection}
                                                        calculateSectionTotalsForDisplay={calculateSectionTotalsForDisplay}
                                                    />
                                                );
                                            } else {
                                                // Render line item card
                                                return (
                                                    <div
                                                        key={`lineitem-${item.data.id}`}
                                                        className="border border-gray-200 rounded-lg p-5 shadow-sm bg-white"
                                                    >
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                                                            <div className="flex-1">
                                                                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">{item.data.itemName}</h3>
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    {item.data.isTaxable === true && (
                                                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">Taxable</span>
                                                                    )}
                                                                </div>
                                                                {item.data.description && (
                                                                    <div
                                                                        className="text-sm sm:text-base text-gray-600 prose prose-sm max-w-none mb-2"
                                                                        dangerouslySetInnerHTML={{ __html: formatDescription(item.data.description) }}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="flex space-x-2 sm:space-x-3 justify-end sm:justify-start">
                                                                {/* Edit Button */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleOpenLineItemModal(item.data)}
                                                                    className="p-3 sm:p-2 bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                                                                    aria-label={`Edit ${item.data.itemName}`}
                                                                    title="Edit Line Item"
                                                                >
                                                                    <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                                </button>
                                                                {/* Duplicate Button */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDuplicateRegularLineItem(item.data.id)}
                                                                    className="p-3 sm:p-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                                                                    aria-label={`Duplicate ${item.data.itemName}`}
                                                                    title="Duplicate Line Item"
                                                                >
                                                                    <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v4a2 2 0 002 2h4m-4 0h4m-4 0v4m0 0h6m-6 0h6M5 12h-.01M17 12h-.01M12 5h.01M12 17h.01M12 7V5m0 0H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                                </button>
                                                                {/* Remove Button */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveRegularLineItem(item.data.id)}
                                                                    className="p-3 sm:p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                                                                    aria-label={`Remove ${item.data.itemName}`}
                                                                    title="Remove Line Item"
                                                                >
                                                                    <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-end items-center border-t border-gray-100 pt-3 mt-3">
                                                            <span className="text-lg font-semibold text-gray-800"></span>
                                                            <span className="text-2xl sm:text-xl font-extrabold text-primary-700 ml-2">
                                                                ${parseFloat(item.data.price || 0).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        });
                                    })()}
                                </div>
                            )}
                        </div>

                        <div className="pt-6 border-t border-slate-200"><QuoteCalculationsDisplay totals={overallTotals} /></div>
                        <div className="pt-6 border-t border-slate-200">
                            <h3 className="text-xl font-semibold text-slate-800 mb-3">Terms & Conditions</h3>
                            <textarea id="quoteTermsConditions" name="quoteTermsConditions" value={formData.quoteTermsConditions} onChange={handleQuoteFieldChange} rows="8" placeholder="Enter specific terms and conditions for this quote." className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm"></textarea>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label htmlFor="overallTaxRate" className="block text-sm font-medium text-slate-700">Tax Rate (%)</label>
                                <input type="number" id="overallTaxRate" name="overallTaxRate" value={formData.overallTaxRate * 100} onChange={(e) => handleQuoteFieldChange({ target: { name: 'overallTaxRate', value: parseFloat(e.target.value) / 100 || 0 } })} className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm text-right" min="0" step="0.1" />
                            </div>
                            <div>
                                <label htmlFor="discountValue" className="block text-sm font-medium text-slate-700">Discount Value</label>
                                <input type="number" id="discountValue" name="discountValue" value={formData.discountValue} onChange={handleQuoteFieldChange} className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm text-right" min="0" step="0.01" />
                            </div>
                            <div>
                                <label htmlFor="discountType" className="block text-sm font-medium text-slate-700">Discount Type</label>
                                <select id="discountType" name="discountType" value={formData.discountType} onChange={handleQuoteFieldChange} className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm bg-white">
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount ($)</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="expirationDate" className="block text-sm font-medium text-slate-700">Expiration Date</label>
                            <input type="date" id="expirationDate" name="expirationDate" value={formData.expirationDate} onChange={handleQuoteFieldChange} className="mt-1 block w-full p-3 border border-slate-300 rounded-md shadow-sm" />
                        </div>

                        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-slate-200">
                            <Link to="/quotes" className="w-full sm:w-auto text-center px-6 py-3 bg-slate-200 text-slate-800 font-medium rounded-lg shadow-md hover:bg-slate-300">Cancel</Link>

                            {isEditing && formData.status !== 'accepted' && (
                                <button
                                    type="button"
                                    onClick={handleManualAccept}
                                    className="w-full sm:w-auto flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-green-600 hover:bg-green-700"
                                    disabled={loading}
                                >
                                    Accept without Signature
                                </button>
                            )}

                            <button type="submit" className="w-full sm:w-auto flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700" disabled={loading}>
                                {isEditing ? 'Save Changes' : 'Create Quote'}
                            </button>
                            {isEditing && (
                                <button type="button" onClick={handleSendEmail} className="w-full sm:w-auto flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={loading || !formData.publicAccessToken}>
                                    Send Quote
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {currentEditingSection && (
                    <QuoteSectionEditorModal
                        isOpen={isSectionEditorModalOpen}
                        onClose={() => setIsSectionEditorModalOpen(false)}
                        onSave={handleSaveSection}
                        initialLineItems={currentEditingSection.lineItems}
                        initialSectionName={currentEditingSection.name}
                        initialSectionDescription={currentEditingSection.description}
                    />
                )}

                <RegularLineItemModal
                    isOpen={isLineItemModalOpen}
                    onClose={() => {
                        setIsLineItemModalOpen(false);
                        setCurrentEditingLineItem(null);
                    }}
                    onSave={handleSaveLineItem}
                    initialItem={currentEditingLineItem}
                />

                {isTemplateSelectorModalOpen && (
                    <TemplateSelectionModal
                        isOpen={isTemplateSelectorModalOpen}
                        onClose={() => setIsTemplateSelectorModalOpen(false)}
                        onSelectTemplate={(templateId, dimensions) => handleAddSection(templateId, dimensions)}
                        onAddCustomSection={(dimensions) => handleAddSection(null, dimensions)}
                        templates={templates}
                    />
                )}

                {showEmailModal && formData && formData.publicAccessToken && (() => {
                    const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));
                    const publicUrl = `${window.location.origin}/quotes/public/${formData.publicAccessToken}`;

                    return (
                        <MessageModal
                            isOpen={showEmailModal}
                            onClose={() => setShowEmailModal(false)}
                            templateType="quote_sent"
                            recipientEmail={selectedClient?.email || ''}
                            contextData={{
                                client_name: selectedClient?.name || '',
                                project_name: formData?.quoteName || 'Your Quote',
                                quote_link: publicUrl,
                                company_name: user?.companyName || user?.username || ''
                            }}
                            quoteId={id}
                            opportunityId={null}
                            onSuccess={() => {
                                setSuccess('Quote sent successfully!');
                                setShowEmailModal(false);
                            }}
                        />
                    );
                })()}
            </div>
        </div>
    );
}

const formatDescription = (description) => {
    if (!description) return '';

    // Apply styling to unordered lists - let CSS handle nested levels
    let formatted = description.replace(/<ul[^>]*>/g, '<ul>');

    // Apply styling to ordered lists - let CSS handle nested levels
    formatted = formatted.replace(/<ol[^>]*>/g, '<ol>');

    // Remove inline styles from list items that might override our CSS
    formatted = formatted.replace(/<li[^>]*style="[^"]*"[^>]*>/g, (match) => {
        return match.replace(/style="[^"]*"/g, '');
    });

    // Clean up any Quill-specific classes that might interfere
    formatted = formatted.replace(/class="ql-indent-\d+"/g, '');

    return formatted;
};

export default QuoteFormPage;
